/**
 * Build a `CrmCreateConfig` from the flat property / association name lists
 * that the list page, pipeline, entity registry and association pickers
 * already carry. One unlabelled section: properties first, then
 * associations — the shape the generic create form has always rendered.
 */

import type { DraftParentContext } from '@/lib/crm/core/draftEntities';

import type { CrmCreateConfig, CrmCreateField } from './types';

export interface GenericCreateConfigArgs {
  orgId: number;
  entityType: string;
  title: string;
  /** Property type names, e.g. `['ahoy_first_name', 'ahoy_email']`. */
  createProperties: readonly string[];
  /** Association type names, e.g. `['ahoy_contact_to_company']`. */
  createAssociations?: readonly string[];
  onSubmit: CrmCreateConfig['onSubmit'];
  submitLabel?: string;
  initialValues?: Record<string, unknown>;
  showCreateAnother?: boolean;
  onPropertyChange?: CrmCreateConfig['onPropertyChange'];
  onCreated?: CrmCreateConfig['onCreated'];
  draftParentContext?: DraftParentContext;
}

/** Id of the single section the generic config produces. */
export const GENERIC_CREATE_SECTION_ID = 'fields';

export function buildGenericCreateConfig({
  orgId,
  entityType,
  title,
  createProperties,
  createAssociations = [],
  onSubmit,
  submitLabel,
  initialValues,
  showCreateAnother,
  onPropertyChange,
  onCreated,
  draftParentContext,
}: GenericCreateConfigArgs): CrmCreateConfig {
  const fields: CrmCreateField[] = [
    ...createProperties.map(
      (property): CrmCreateField => ({ kind: 'property', property })
    ),
    ...createAssociations.map(
      (associationType): CrmCreateField => ({
        kind: 'association',
        associationType,
      })
    ),
  ];
  return {
    orgId,
    entityType,
    title,
    submitLabel,
    sections: [{ id: GENERIC_CREATE_SECTION_ID, fields }],
    // The type-agnostic path: the ontology decides which kinds this flow may
    // create. Bespoke configs opt out by construction (see the field's docs).
    enforceGenericCreatableKinds: true,
    initialValues,
    onSubmit,
    onCreated,
    showCreateAnother,
    onPropertyChange,
    draftParentContext,
  };
}
